//src/components/common/PrivacyPolicy/CompanyTermsAndPolicy/TermsAndPolicyBody.jsx
import React from "react";

const contentMap = {
  privacy: {
    mainTitle: "Privacy Policy",
    sections: [
      {
        title: "Introduction",
        paragraph:
          'Welcome to myassessment.ai , a service provided by TakshkAI HR Solution Private Limited ("we," "our," "us"). By accessing or using our website and services, you agree to comply with and be bound by the following terms and conditions ("Terms"). Please read these Terms carefully. If you do not agree to these Terms, please do not use our services.',
      },
      {
        title: "Definitions",
        paragraph:
          "Website: Refers to myassessment.ai. User: Refers to both assessment facilitators and participants using our services."
      },
      {
        title: "Services",
        subSections: [
          {
            label: "A. Project & Material Upload",
            type: "group",
            content: {
              Input: [
                "Assessment facilitator creates a project (public or private) and uploads learning materials (doc, ppt, pdf, etc.).",
                "AI extracts content, generates summaries, and drafts potential questions based on the materials.",
              ],
              Output: [
                "Project dashboard lists all projects with status indicators.",
                "Uploaded materials become reusable assets for assessments across projects.",
              ],
            },
          },
          {
            label: "B. AI-Powered Assessment Creation",
            type: "group",
            content: {
              Input: [
                "Assessment facilitator chooses AI-suggested questions or manually writes questions.",
                "Sets difficulty, selects sections (subjective, objective, conversational AI), and assigns marks.",
              ],
              Output: [
                "Assessment codes (pre- and/or post-assessment) are generated automatically.",
                "Final question set is stored and linked to the project, ready for participants.",
              ],
            },
          },
          {
            label: "C. Participant Access & Testing Flow",
            type: "group",
            content: {
              Input: [
                "Participant enters the assessment code on landing page, then provides name (and optional email).",
                "Assessment sections appear in the defined order (subjective → objective → conversational AI).",
              ],
              Output: [
                "Real-time progress tracking and timer per section.",
                "Assessment responses saved securely for scoring.",
              ],
    },
  },
  {
    label: "D. Results & Analytics Dashboard",
    type: "group",
    content: {
      Input: [
        "Assessment submissions and AI-generated scores feed into the analytics engine.",
      ],
      Output: [
        "Live leaderboards and detailed reports for pre, post, and comparative results.",
        "Downloadable PDFs of individual reports; percentile comparison to cohort.",
        "Play/Stop controls and share buttons (WhatsApp, Email, Screen View) for easy distribution of assessment links and codes.",
      ],
    },
  },
  {
    label: "E. Assessment Facilitator Repository (Dossier-Style)",
    type: "contents",
    items: [
      "Uploaded learning materials.",
      "Full question banks and revisions.",
      "Assessment results with leaderboards.",
      "AI summaries and analytics exports.",
        ],
      },
    ],
      },
      {
        title: "User Obligations",
        subSections: [
          {
            label: "A. Participant Obligations",
            items: [
              "Provide accurate and truthful information when registering",
              "Maintain the confidentiality of login credentials.",
              "Use our services in compliance with applicable laws and regulations.",
            ],
          },
          {
            label: "B. Assessment Facilitator Obligations",
            items: [
              "Provide accurate and truthful information when registering",
              "Maintain the confidentiality of login credentials.",
              "Use our services in compliance with applicable laws and regulations.",
              "Ensure that all uploaded materials are appropriate, non-obscene, and relevant to assessments. Facilitators must understand the content they upload. Violation of this clause may result in permanent suspension from the platform."
            ],
          },
        ],
      },
      {
        title: "Data Protection",
        subSections: [
          {
            label: "A. Security Measures",
            items: [
              "We use SSL encryption to protect sensitive data.",
              "Data is stored securely in encrypted servers.",
            ],
          },
          {
            label: "B. Data Retention",
            items: [
              "Personal information is retained as long as necessary for business and legal purposes.",
              "You may request data deletion via our contact form.",
            ],
          },
        ],
      },
      {
        title: "Data Collection and Privacy",
        paragraph:
          "We collect and use personal information as described in our Privacy Policy. By using our services, you consent to the collection and use of your information as outlined in the Privacy Policy.",
      },
      {
        title: "Intellectual Property",
        paragraph:
          "All content, trademarks, and data on myassessment.ai, including but not limited to software, databases, text, graphics, icons, hyperlinks, and designs, are the property of TakshkAI HR Solution Private Limited or have been licensed to us. You are not granted any rights to use our intellectual property other than as set out in these Terms.",
      },
      {
        title: "Limitation of Liability",
        paragraph:
          "To the maximum extent permitted by applicable law, TakshkAI HR Solution Private Limited shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages, or any other damages of any kind, arising out of or related to your use of our services.",
      },
      {
        title: "Indemnification",
        paragraph:
          "You agree to indemnify, defend, and hold harmless TakshkAI HR Solution Private Limited, its officers, directors, employees, agents, licensors, and suppliers from and against all claims, losses, expenses, damages, and costs, including reasonable attorneys fees, resulting from any violation of these Terms or any activity related to your account by you or any other person accessing the Website using your account.",
      },
      {
        title: "Termination",
        paragraph:
          "We reserve the right to terminate or suspend your access to our services, without prior notice or liability, for any reason, including but not limited to your breach of these Terms.",
      },
      {
        title: "Governing Law",
        paragraph:
          "These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions.",
      },
      {
        title: "Changes to Terms",
        paragraph:
          "We reserve the right to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on our Website. Your continued use of the services following the posting of any changes constitutes acceptance of those changes.",
      },
      {
        title: "Contact Us",
        paragraph:
          "If you have any questions about these Terms, please contact us at: support@hireasy.ai",
      },
    ],
  },

  terms: {
    mainTitle: "Terms and Conditions",
    sections: [
      {
        title: "Introduction",
        paragraph:
          'TakshkAI HR Solution Private Limited ("we", "our", "us") operates the website myassessment.ai (the "Website"). This Privacy Policy outlines our policies and practices regarding the collection, use, and disclosure of personal information when you use our Website and services.',
      },
      {
        title: "Information We Collect",
        paragraph:
          "We collect personal information such as your name and email address, along with any learning materials you upload to the platform.",
        subSections: [],
      },
      {
        title: "How We Use Your Information",
        paragraph:
        "We use your information to create and manage your account, authenticate your identity, and personalize your experience. This includes storing uploaded materials, enabling secure logins, and providing essential communications related to your usage.",
        subSections: [],
      },
      {
        title: "Data Protection and Security",
        paragraph:
          "We implement appropriate technical and organizational measures to protect personal information against unauthorized access, alteration, disclosure, or destruction.",
      },
      {
        title: "Sharing and Disclosure of Information",
        paragraph:
          "We do not share personal information with third parties except in the following circumstances:",
        subSections: [
          {
            items: [
              "With service providers who perform services on our behalf",
              "To comply with legal obligations and requests from law enforcement authorities",
            ],
          },
        ],
      },
      {
        title: "Cookies and Tracking Technologies",
        paragraph:
          "Our Website uses cookies to enhance user experience, track usage, and provide relevant advertisements. You can manage your cookie preferences through your browser settings.",
      },
      {
        title: "Legal Basis for Processing Personal Information",
        paragraph:
          "We process personal information based on the following legal grounds:",
          subSections: [
            {
              items: [
                "Consent: When you provide us with consent to process your information for specific purposes.",
                "Legal Obligation: To comply with legal obligations and regulatory requirements.",
              ],
            },
          ],
      },
      {
        title: "Your Rights",
        paragraph:
          'You have the right to access, rectify, update, or delete your personal information. You can exercise these rights by contacting us using the information provided in the "Contact Us" section.',
      },
      {
        title: "Retention of Personal Information",
        paragraph:
          'We retain personal information for as long as necessary to fulfill the purposes for which it was collected and to comply with legal, regulatory, or contractual obligations.',
      },
      {
        title: "Changes to This Privacy Policy",
        paragraph:
          'We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.',
      },
      {
        title: "Contact Us",
        paragraph:
          "If you have any questions about these Terms, please contact us at: support@hireasy.ai",
      },
    ],
  },

  return: {
    mainTitle: "Return and Cancellation Policy",
    sections: [
      {
        title: "Introduction",
        paragraph:
          'TakshkAI HR Solution Private Limited ("we", "our", "us") operates the website myassessment.ai (the "Website"). This Privacy Policy outlines our policies and practices regarding the collection, use, and disclosure of personal information when you use our Website and services.',
      },
      {
        title: "Services Provided",
        subSections: [
          {
            label: "A. Project & Material Upload",
            type: "group",
            content: {
              Input: [
                "Assessment facilitator creates a project (public or private) and uploads learning materials (doc, ppt, pdf, etc.).",
                "AI extracts content, generates summaries, and drafts potential questions based on the materials.",
              ],
              Output: [
                "Project dashboard lists all projects with status indicators.",
                "Uploaded materials become reusable assets for assessments across projects.",
              ],
            },
          },
          {
            label: "B. AI-Powered Assessment Creation",
            type: "group",
            content: {
              Input: [
                "Assessment facilitator chooses AI-suggested questions or manually writes questions.",
                "Sets difficulty, selects sections (subjective, objective, conversational AI), and assigns marks.",
              ],
              Output: [
                "Assessment codes (pre- and/or post-assessment) are generated automatically.",
                "Final question set is stored and linked to the project, ready for participants.",
              ],
            },
          },
          {
            label: "C. Participant Access & Testing Flow",
            type: "group",
            content: {
              Input: [
                "Participant enters the assessment code on landing page, then provides name (and optional email).",
                "Assessment sections appear in the defined order (subjective → objective → conversational AI).",
              ],
              Output: [
                "Real-time progress tracking and timer per section.",
                "Assessment responses saved securely for scoring.",
              ],
            },
          },
        {
        label: "D. Results & Analytics Dashboard",
        type: "group",
        content: {
          Input: [
            "Assessment submissions and AI-generated scores feed into the analytics engine.",
          ],
          Output: [
            "Live leaderboards and detailed reports for pre, post, and comparative results.",
            "Downloadable PDFs of individual reports; percentile comparison to cohort.",
            "Play/Stop controls and share buttons (WhatsApp, Email, Screen View) for easy distribution of assessment links and codes.",
          ],
        },
      },
      {
        label: "E. Assessment facilitator Repository (Dossier-Style)",
        type: "contents",
        items: [
          "Uploaded learning materials.",
          "Full question banks and revisions.",
          "Assessment results with leaderboards.",
          "AI summaries and analytics exports.",
            ],
          },
        ],
      },
      {
        title: "Refund Policy",
        subSections: [
          {
            label: "A. No Refunds",
            items: [
              "In the scenario of duplicate / erroneous charge, refund initiation by myassessment.ai will take 1 business day of receiving the request. Refund credit to customer: In the scenario of UPI/Wallet transactions, it will take 1 business day, whereas transactions initiated by Cards / Net-banking will take 5-7 business days. For failed transactions no amount is captured, credits are allocated instantly on successful payments, so normal completed transactions are final and non-refundable.",
              "Refund eligibility checked against usage logs.",
            ],
          },
          {
            label: "B. Service Issues",
            items: [
              "In the event that the service is not to the satisfaction of the customer, we will take the complaint and provide a resolution to the problem. Please contact our support team at [support@hireasy.ai] with details of the issue, and we will work with you to resolve it promptly.",
            ],
          },
        ],
      },
      {
        title: "Changes to This Privacy Policy",
        subSections: [
          {
            label: "A. Service Cancellation",
            items: [
              "As our services are provided on a per-use basis, there is generally no need for a cancellation process. However, if you wish to cancel a purchase, please notify us as soon as possible and you may do so only if participants have not already participated in assessments.",
            ],
          },
          {
            label: "B. Process for Cancellation",
            items: [
              "To cancel a purchase or a transaction, please contact our support team at [support@hireasy.ai] with your request and the details of the service to be cancelled. We will confirm the cancellation and notify you of any further steps if necessary.",
            ],
          },
        ],
      },
      {
        title: "Contact Us",
        paragraph:
          "If you have any questions about these Terms, please contact us at: support@hireasy.ai",
      },
    ],
  },
};

const TermsAndPolicyBody = ({ activeTab }) => {
  const current = contentMap[activeTab];

  return (
    <section className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <div className="container max-w-5xl px-4 py-12 mx-auto">
        <h6 className="text-center w-fit mx-auto px-8 py-2 bg-[#6c63ff] rounded-3xl text-white text-sm mb-4">{current.mainTitle}</h6>
        <h2 className="text-3xl font-semibold text-center mb-4">Our {current.mainTitle}</h2>
        <p className="text-sm text-center mb-20 text-gray-600">Read our Terms below to learn more about your rights and responsibilities as a MyAssessment.ai user.</p>
        <div className="grid gap-4 mx-4 sm:grid-cols-12">
          <div className="relative col-span-12 px-4 space-y-6">
            <div className="col-span-12 space-y-12 relative px-4 sm:space-y-8 sm:before:absolute sm:before:top-2 sm:before:bottom-0 sm:before:w-0.5 sm:before:-left-3 before:bg-gray-300 before:dark:bg-gray-600">
              {current.sections.map((section, sectionIndex) => (
                <div
                  key={sectionIndex}
                  className="flex flex-col sm:relative sm:before:absolute sm:before:top-2 sm:before:w-4 sm:before:h-4 sm:before:rounded-full sm:before:left-[-35px] sm:before:z-[1] before:bg-violet-600 before:dark:bg-violet-500"
                >
                  <h3 className="text-2xl font-semibold tracking-wide">
                    {section.title}
                  </h3>
                  {section.paragraph && (
                    <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                      {section.paragraph}
                    </p>
                  )}

                  {section.subSections?.map((sub, subIndex) => (
                    <div key={subIndex} className="mt-5">
                      <h4 className="font-semibold text-base text-gray-800 dark:text-gray-200 mb-2">
                        {sub.label}
                      </h4>

                      {/* Grouped Input/Output style */}
                      {sub.type === "group" && (
                        <div className="space-y-4 text-sm">
                          {sub.content.Input && (
                            <div>
                              <p className="font-semibold mb-1">Input:</p>
                              <ul className="list-disc list-inside ml-4">
                                {sub.content.Input.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {sub.content.Output && (
                            <div>
                              <p className="font-semibold mt-3 mb-1">Output:</p>
                              <ul className="list-disc list-inside ml-4">
                                {sub.content.Output.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Simple bullet list (contents or items) */}
                      {(sub.type === "contents" || !sub.type) && (
                        <ul className="list-disc list-inside ml-4 text-sm">
                          {sub.items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsAndPolicyBody;